import { useRef, useCallback, useEffect } from 'react';
import { accept } from '../utils/event.js';
import { isArrowKey } from '../utils/key-code.js';
import { PressedKeys } from '../utils/pressed-keys.js';
import { fromPressedArrowKeys, isZero, ZERO } from '../utils/vector.js';

/**
 * Sets up a handler for continuous arrow key presses in all directions.
 *
 * The typical behavior of an OS is that only one key can be repeating at a time. If you press
 * another key while one is repeating, the first stops repeating and the second starts. If the
 * second key is released before the first, the first does not resume repeating. This hook makes
 * that work, so held arrow keys are always repeated. The repeat rate is determined by keeping
 * track of the timing between repeated keydown events and averaging them.
 */
function useArrowKeyHandler(ctx) {
    const interval = useRef(null);
    const ctxRef = useRef(ctx);
    ctxRef.current = ctx;
    const clear = useCallback(() => {
        clearInterval(interval.current);
        interval.current = null;
    }, []);
    // clear is a stable function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => clear, []);
    function run(e) {
        const arrows = PressedKeys.arrows();
        // This can happen if the browser is blurred while keys are held and PressedKeys is cleared.
        if (!e && arrows.length === 0) {
            clear();
            return;
        }
        const delta = e ? deltaFromKeyboardEvent(e) : fromPressedArrowKeys(arrows);
        if (!isZero(delta))
            ctxRef.current.onDelta(delta);
    }
    return {
        onKeyDown(e) {
            if (e.key === 'Meta') {
                // If the Meta key is held, we have to clear the repeating interval because we won't
                // get any keyup events for the other keys until Meta is released.
                clear();
            }
            if (e.key === 'Tab' && interval.current) {
                // If the user tabs away while holding arrow keys, we need to stop the repeats so that they
                // don't get stuck on since we won't see keyup events anymore.
                clear();
                return;
            }
            if (!isArrowKey(e.code))
                return;
            const result = ctxRef.current.onKeyDown?.(e);
            if (result === false)
                return;
            accept(e);
            if (interval.current)
                return;
            run(e);
            if (e.repeat) {
                const repeatRate = PressedKeys.repeatRate();
                if (repeatRate > 0) {
                    interval.current = window.setInterval(run, repeatRate);
                }
            }
        },
        onKeyUp(e) {
            if (!isArrowKey(e.code))
                return;
            if (!PressedKeys.anyArrows()) {
                clear();
            }
        },
    };
}
function deltaFromKeyboardEvent(e) {
    return isArrowKey(e.code) ? fromPressedArrowKeys([e.code]) : ZERO;
}

export { useArrowKeyHandler };
//# sourceMappingURL=use-arrow-key-handler.js.map
